import React, { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { GroupBase, OptionsOrGroups } from "react-select";
import { useLazyOrganizationGroupsQuery } from "../../../api/network/adminApiServices";
import { setModalsData, TModalsState } from "../../../api/store/commonSlice";
import {
  LegacyGroupEntityListingPayload,
  OrgGHGroup,
  TLegacyGroup,
} from "../../../api/types/Group";
import FormActionButtons from "../../../components/admin/formActionButtons";
import {
  AdminFormFieldAsyncLazyDropdown,
  AdminFormFieldPillItem,
  AdminFormFieldPillList,
  PillItem,
  PseudoSelect,
  TSelectboxOption,
} from "../../../components/admin/formFields";
import HierarchyGroupSelector from "../../../components/common/hierarchyGroupSelector";
import { APP_CONFIG } from "../../../constants/constants";
import { asyncDebounce } from "../../../utils/common";
import { useLoggedInUserData } from "../../../utils/user";
import { HeatmapAdminGeneralDetailFormProps } from "./heatmap-admin-general";

export const HeatmapAdminAuthorizedGroupsForm: FC<
  HeatmapAdminGeneralDetailFormProps
> = ({
  values,
  errors,
  touched,
  handleChange,
  formikSetValue,
  handleBlur,
  formikSetTouched,
  userCanEdit,
  loadingData,
}) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "admins.heatmap",
  });
  const { t: tMain } = useTranslation();
  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );
  const dispatch = useDispatch();

  // check if group hierarchy is enabled
  const accountHasGroupHierarchyEnabled = useLoggedInUserData("groupHierarchy");

  // prepare group query params
  const thisUserOrganizationId = useLoggedInUserData("ownerOrganizationId");
  const [orgGroupsQueryParams, setOrgGroupsQueryParams] =
    React.useState<LegacyGroupEntityListingPayload>({
      accountid: `${thisUserOrganizationId}`,
      page: 1,
      page_size: APP_CONFIG.LISTINGS.DEFAULT_PAGE_SIZE,
      search: "",
    });

  // // fetch organization groups
  // const { data: initialGroups, isFetching: isFetchingInitialGroups } =
  //   useOrganizationGroupsQuery(orgGroupsQueryParams);

  // fetch searched groups
  const [orgGroupsApiTrigger, { isFetching: isFetchingGroups }] =
    useLazyOrganizationGroupsQuery();

  const loadOptionsHandlerGroups = async (
    inputValue: string,
    prevOptions: OptionsOrGroups<unknown, GroupBase<unknown>>,
    additional?: { page: number }
  ) => {
    const newOrgGroupsQueryParams = {
      ...orgGroupsQueryParams,
      search: inputValue,
      search_fields: APP_CONFIG.ENTITY_SEARCH_FIELDS.GROUP_DROPDOWN.join(","), // search by groupid only
      page: (additional?.page as number) ?? 2,
    };
    setOrgGroupsQueryParams(newOrgGroupsQueryParams);
    const { data: groupsResponse } = await orgGroupsApiTrigger(
      newOrgGroupsQueryParams
    );
    if (APP_CONFIG.DEBUG.ALL) console.log("groupsResponse", groupsResponse);
    if (!groupsResponse || !groupsResponse?.results) {
      return {
        options: [],
        hasMore: false,
        additional: {
          page: 2,
        },
      };
    }
    const { results } = groupsResponse as { results: TLegacyGroup[] };
    return {
      options: !!results
        ? (results || ([] as TLegacyGroup[]))
            // ?.filter(
            //   // (item) => !values.groups.includes(`${item.groupid}`)
            //   (item) =>
            //     values.groups.find((_item) => _item.group_id !== item.groupid)
            // )
            .map((item: TLegacyGroup, index: number) => ({
              value: `${item.groupid}`,
              label: item.displayname || item.groupid,
            }))
        : // .filter((item: TSelectboxOption) =>
          //   // !values.groups.includes(item.value)
          //   values.groups.find((_item) => _item.group_id !== item.value)
          // )
          [],
      hasMore: groupsResponse?.next ? true : false,
      additional: {
        page: (additional?.page as number) + 1 || 2,
      },
    };
  };

  const debouncedLoadOptionsHandlerGroups = asyncDebounce(
    loadOptionsHandlerGroups,
    APP_CONFIG.DEBOUNCE.SEARCH
  );

  // const handleGroupChange = async (e: any) => {
  //   const newlySelectedGroup = e.value;
  //   if (!!e.value) {
  //     formikSetValue("groups", [
  //       ...values.groups,
  //       newlySelectedGroup,
  //     ]);
  //   }
  // };
  const handleGroupChange = async (e: any) => {
    const newlySelectedGroup = e;
    if (!!e) {
      formikSetValue("groups", [
        ...newlySelectedGroup.map((_item: TSelectboxOption) => ({
          group_id: _item.value,
        })),
      ]);
    }
  };

  // add all groups to vehicle
  const addAllGroups = () => {
    formikSetValue("groups", []);
  };

  // handle group removal
  const onRemoveFromGroup = (option: any) => {
    formikSetValue(
      "groups",
      values.groups.filter(
        (selectedGroup: any) => selectedGroup.group_id !== option
      )
    );
  };

  const MemoizedFormActionSelectGroupsButton = useMemo(() => {
    return (
      <>
        <FormActionButtons
          buttons={[
            {
              label: tMain("select_groups_button"),
              type: "button",
              variant: !!errors.groups ? "danger" : "primary-like",
              customWrapperClass: "col-span-3",
              onClick: () => {
                dispatch(
                  setModalsData({
                    ...modalsState,
                    showHierarchyGroupSelector: true,
                  })
                );
              },
              disabled: !userCanEdit || loadingData, // disable if user cannot edit or all groups is selected
            },
          ]}
        />
      </>
    );
  }, [userCanEdit, loadingData, modalsState, errors.groups]);

  return (
    <div className="px-5 pt-4 pb-8 bg-gray-100 grid grid-cols-12 gap-4">
      {loadingData ? (
        <PseudoSelect label={t("select_groups")} />
      ) : (
        <>
          {accountHasGroupHierarchyEnabled === "1" ? (
            <>
              {/* <AdminFormFieldSubmit
                  label={tMain('select_groups_button')}
                  type="button"
                  variant="primary-like"
                  customWrapperClass="col-span-3"
                  onClick={() => { dispatch(setModalsData({ ...modalsState, showHierarchyGroupSelector: true, })) }}
                  disabled={!userCanEdit || loadingData || !!values.all_groups} // disable if user cannot edit or all groups is selected
                /> */}
              {MemoizedFormActionSelectGroupsButton}
              {modalsState.showHierarchyGroupSelector && (
                <HierarchyGroupSelector
                  initiallySelectedGroups={values.groups.map(
                    (item) => item.group_id
                  )}
                  onSelectionChange={(selectedGroups: string[]) => {
                    if (APP_CONFIG.DEBUG.DISPLAY_VERBOSE)
                      console.log("VALUES RECEIVED:", selectedGroups);
                    formikSetValue(
                      "groups",
                      selectedGroups?.map((item) => ({
                        group_id: `${item}`,
                      })) || []
                    );
                    setTimeout(() => {
                      formikSetTouched("groups");
                    }, 100); // to trigger validation
                  }}
                />
              )}
            </>
          ) : (
            <AdminFormFieldAsyncLazyDropdown
              customWrapperClass="col-span-12"
              key={`group_selector_${JSON.stringify(values.groups)}`} // causes re-render on group selection
              loadingData={loadingData || isFetchingGroups}
              label={t("select_groups")}
              id="group_selector"
              name="group_selector"
              noOptionsMessage={() => t("group_selector_no_options")}
              // defaultOption={{ value: `${values.group_selector}`, label: `${groupDisplayName(`${values.streetaddress}`, "-")}` }}
              // defaultOptions={initialGroups?.results
              //   ?.filter(
              //     (item) => !values.groups.includes(`${item.groupid}`)
              //   )
              //   ?.map((item: TLegacyGroup) => ({
              //     value: `${item.groupid}`,
              //     label: item.displayname || item.groupid,
              //   }))}
              isMulti={true}
              placeholder={t("select_groups_placeholder")}
              additional={{ page: 1 }}
              loadOptions={debouncedLoadOptionsHandlerGroups}
              onChange={handleGroupChange}
              onBlur={(e) => {
                formikSetTouched("groups", true);
                handleBlur(e);
              }}
              error={
                typeof errors.groups === "string" ? errors.groups : undefined
              }
              value={
                values.groups.map((_item) => ({
                  label: _item.group_id,
                  value: _item.group_id,
                })) as TSelectboxOption[]
              }
              touched={touched.group_selector}
              disabled={!userCanEdit || loadingData} // || values.all_groups
              detailsFormField={true}
              menuPlacement="top"
            />
          )}
        </>
      )}

      {/* hierarchy pill list and error message */}
      {accountHasGroupHierarchyEnabled === "1" && (
        <>
          <div className="col-span-12 grid grid-cols-12">
            <AdminFormFieldPillList
              disabled={!userCanEdit} // || values.all_groups
              isError={!!errors.groups && userCanEdit}
            >
              {values.groups.map(
                (
                  group,
                  i: number // (group: string | { label: string; value: string }, i: number) => (
                ) => (
                  <AdminFormFieldPillItem // TODO: Fix prefill on change item from left column
                    key={`pillItem_${i}`}
                    disabled={!userCanEdit} // || values.all_groups
                    item={
                      {
                        name:
                          typeof group === "string" ? group : group.group_id,
                      } as PillItem
                    }
                    onRemove={() =>
                      onRemoveFromGroup(
                        typeof group === "string" ? group : group.group_id
                      )
                    }
                    onItemClick={
                      accountHasGroupHierarchyEnabled === "1"
                        ? (item) => {
                            dispatch(
                              setModalsData({
                                ...modalsState,
                                showReadOnlyViewForHierarchyGroup: {
                                  accountid: thisUserOrganizationId,
                                  active_inactive_vehicle_counts: {
                                    active_vehicle_count: 0,
                                    inactive_vehicle_count: 0,
                                  },
                                  description: "",
                                  groupid:
                                    typeof group === "string"
                                      ? group
                                      : group.group_id,
                                } as OrgGHGroup,
                                showHierarchyGroupSelector: true,
                              })
                            );
                          }
                        : undefined
                    }
                  />
                )
              )}
            </AdminFormFieldPillList>
          </div>

          {/* error display */}
          {!!errors.groups &&
            typeof errors.groups === "string" &&
            userCanEdit && (
              <div className="col-span-12 bg-field-error-light px-2 py-1 rounded text-field-error-dark text-xs">
                {errors.groups}
              </div>
            )}
        </>
      )}
    </div>
  );
};
